import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowPathIcon, InformationCircleIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import preopService from '../../services/preopService';
import { errText } from '../../services/scheduleService';
import { isAdmin } from '../../utils/permissions';
import PreopPanel, { PreopBadge } from '../../components/preop/PreopPanel';

const VIEWS = [
  { key: 'waiting', label: 'Waiting for the patient', tone: 'text-blue-700' },
  { key: 'to_review', label: 'Back — to review', tone: 'text-teal-700' },
  { key: 'expired', label: 'Link expired', tone: 'text-amber-700' },
  { key: 'reviewed', label: 'Reviewed', tone: 'text-green-700' },
  { key: 'all', label: 'All', tone: 'text-slate-700' },
];
const fmtDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' }) : '—');
const fmtWhen = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) : '—');

/**
 * Pre-op questionnaires — the health questions sent to booked patients before
 * surgery: who still has to answer, whose answers are back and waiting for the
 * anaesthetist, and whose links expired. Questionnaires are sent from a booked
 * patient on the Theatre Schedule.
 */
const PreopList = () => {
  const admin = isAdmin();
  // The dashboard can open a tab, or one questionnaire, directly.
  const location = useLocation();
  const [view, setView] = useState(() => location.state?.view || 'waiting');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(() => location.state?.open || null);

  const load = useCallback(async () => {
    try {
      setData(await preopService.list({ view }));
      setError('');
    } catch (e) {
      setError(errText(e));
    }
  }, [view]);
  useEffect(() => { load(); }, [load]);

  const cols = admin ? 7 : 6;

  return (
    <div className="mx-auto max-w-7xl p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-brand-deep">Pre-op questionnaires</h1>
          <p className="mt-1 text-slate-500">Health questions patients answer before their anaesthetic, and what the anaesthetist needs to look at.</p>
        </div>
        {admin && (
          <Link to="/preop-questions" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <PencilSquareIcon className="h-4 w-4" /> Edit the questions
          </Link>
        )}
      </div>

      <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-900">
        <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
        <span>Send a questionnaire from a booked patient on the <b>Theatre Schedule</b> (open the session, then <b>Pre-op</b>). The patient can save and come back; when they send it, the anaesthetist is told on their phone and marks it reviewed.</span>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {VIEWS.map((v) => (
          <button key={v.key} onClick={() => setView(v.key)} className={`rounded-xl border px-3 py-2.5 text-left transition ${view === v.key ? 'border-brand-navy bg-white shadow-sm ring-2 ring-brand-navy/10' : 'border-slate-200 bg-white/70 hover:border-slate-300'}`}>
            <div className={`text-xs font-bold ${v.tone}`}>{v.label}</div>
            <div className="text-lg font-bold text-slate-800">{data ? data.counts[v.key] : '—'}</div>
          </button>
        ))}
      </div>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex justify-end border-b border-slate-100 px-4 py-2">
          <button onClick={load} title="Refresh" className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-brand-navy"><ArrowPathIcon className="h-5 w-5" /></button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2 font-semibold">Surgery</th>
                <th className="px-2 py-2 font-semibold">Patient</th>
                {admin && <th className="px-2 py-2 font-semibold">Anaesthetist</th>}
                <th className="px-2 py-2 font-semibold">Status</th>
                <th className="px-2 py-2 text-right font-semibold">Flags</th>
                <th className="px-2 py-2 text-right font-semibold">BMI</th>
                <th className="px-4 py-2 text-right font-semibold">Sent / back</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.questionnaires || []).map((q) => {
                const back = ['completed', 'reviewed'].includes(q.status);
                return (
                  <tr key={q.preop_id} onClick={() => setOpen(q)} className="cursor-pointer align-top hover:bg-slate-50">
                    <td className="px-4 py-2.5 text-slate-600">{fmtDate(q.service_date)}<div className="text-[11px] text-slate-400">{q.hospital_name}</div></td>
                    <td className="px-2 py-2.5">
                      <div className="font-semibold text-slate-700">{q.patient_name}</div>
                      {q.procedure_text && <div className="max-w-xs truncate text-[11px] text-slate-400">{q.procedure_text}</div>}
                    </td>
                    {admin && <td className="px-2 py-2.5 text-slate-600">{q.doctor}</td>}
                    <td className="px-2 py-2.5"><PreopBadge preop={q} /></td>
                    <td className="whitespace-nowrap px-2 py-2.5 text-right text-xs">
                      {back ? (
                        <>
                          {q.alert_count > 0 && <span className="font-bold text-red-700">{q.alert_count} alert{q.alert_count === 1 ? '' : 's'}</span>}
                          {q.alert_count > 0 && q.caution_count > 0 && ' · '}
                          {q.caution_count > 0 && <span className="font-semibold text-amber-800">{q.caution_count} to check</span>}
                          {!q.alert_count && !q.caution_count && <span className="text-green-700">Nothing flagged</span>}
                        </>
                      ) : '—'}
                    </td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-slate-700">{q.bmi ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-slate-500">
                      {q.completed_at ? `back ${fmtWhen(q.completed_at)}` : q.sent_at ? `sent ${fmtWhen(q.sent_at)}` : '—'}
                      {['sent', 'in_progress'].includes(q.status) && <div className="text-[11px]">{q.last_saved_at ? 'started' : q.opened_at ? 'opened' : 'not opened yet'}</div>}
                    </td>
                  </tr>
                );
              })}
              {data && data.questionnaires.length === 0 && <tr><td colSpan={cols} className="px-4 py-8 text-center text-slate-400">No questionnaires here.</td></tr>}
              {!data && <tr><td colSpan={cols} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {open && (
        <PreopPanel
          bid={open.bid}
          patient={{ patient_id: open.patient_id, name: open.patient_name, email: open.patient_email }}
          bookingLabel={[fmtDate(open.service_date), open.hospital_name, open.surgeon, open.procedure_text].filter(Boolean).join(' · ')}
          onClose={() => { setOpen(null); load(); }}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default PreopList;
